import type { MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { errorMessage } from '../i18n/errorMessages';
// RuntimeCard — the owner's start/stop control for an agent's runtime process.
//
// Blueprint agent-lifecycle.md §2.2 (Borgee ships no runtime; this is a
// process descriptor, not a supervisor) and §2.3 (a fault must explain itself).
//
// Design rules this card holds:
//   - Non-owners get no start/stop button in the DOM at all. Rendering a
//     disabled one would leak who the owner is.
//   - `data-runtime-status` is the four-state process enum. There is no
//     'starting' / 'stopping' intermediate: start and stop are synchronous
//     writes, so a pending state would be fiction.
//   - Raw endpoint_url / last_heartbeat_at never reach the screen — false
//     precision about internals the owner cannot act on. Silence beats
//     synthetic progress.
//   - Failures report inline, never as a toast: runtime state changes travel
//     by system DM, and an owner-initiated click answers where it was clicked.
//   - Runtime reason codes share localized labels with sidebar presence.

import { useCallback, useState } from 'react';
import { REASON_LABELS } from '../lib/agent-state';
import {
  type Agent,
  type AgentRuntime,
  type AgentRuntimeStatus,
  startAgentRuntime,
  stopAgentRuntime,
} from '../lib/api';

const STATUS_LABELS: Record<AgentRuntimeStatus, MessageDescriptor> = {
  registered: msg`Not started`,
  running: msg`Running`,
  stopped: msg`Stopped`,
  error: msg`Error`,
};

const STATUS_TONES: Record<AgentRuntimeStatus, 'ok' | 'muted' | 'error'> = {
  registered: 'muted',
  running: 'ok',
  stopped: 'muted',
  error: 'error',
};

interface Props {
  agent: Agent;
  runtime: AgentRuntime | null;
  // null = unauthenticated / still loading; the owner gate is strict equality
  // against agent.owner_id, so neither null nor undefined renders the buttons.
  viewerUserID: string | null;
  onRefresh: () => void;
}

export default function RuntimeCard({ agent, runtime, viewerUserID, onRefresh }: Props) {
  const { i18n } = useLingui();
  const [busy, setBusy] = useState<'start' | 'stop' | null>(null);
  const [error, setError] = useState<MessageDescriptor | null>(null);

  const isOwner = viewerUserID !== null && viewerUserID === agent.owner_id;

  const handleStart = useCallback(async () => {
    if (busy) return;
    setBusy('start');
    setError(null);
    try {
      await startAgentRuntime(agent.id);
      onRefresh();
    } catch (err) {
      // Owner-initiated failures answer inline (see the header note); runtime
      // state changes travel by system DM instead.
      setError(errorMessage(err, msg`Could not start runtime`));
    } finally {
      setBusy(null);
    }
  }, [agent.id, busy, onRefresh]);

  const handleStop = useCallback(async () => {
    if (busy) return;
    setBusy('stop');
    setError(null);
    try {
      await stopAgentRuntime(agent.id);
      onRefresh();
    } catch (err) {
      setError(errorMessage(err, msg`Could not stop runtime`));
    } finally {
      setBusy(null);
    }
  }, [agent.id, busy, onRefresh]);

  // No runtime registered yet: hide the card entirely. Borgee ships no runtime,
  // so an unregistered agent should not pretend to have one.
  if (!runtime) {
    return null;
  }

  const status = runtime.status;
  const reason = runtime.last_error_reason;
  const reasonLabel = reason ? i18n._(REASON_LABELS[reason] ?? REASON_LABELS.unknown) : null;
  const agentDisabled = agent.disabled;

  return (
    <div className="runtime-card" data-runtime-status={status}>
      <div className="runtime-card-header">
        <strong>
          <Trans>Runtime</Trans>
        </strong>
        <span
          className={`runtime-status-badge runtime-status-${STATUS_TONES[status]}`}
          data-status={status}
        >
          {i18n._(STATUS_LABELS[status])}
        </span>
        {status === 'error' && reason && (
          <span className="runtime-error-reason" data-error-reason={reason}>
            {reasonLabel}
          </span>
        )}
      </div>

      <div className="runtime-card-body">
        <div className="runtime-card-meta">
          {/* process_kind only. Raw endpoint_url / heartbeat timestamps stay
              off screen (see the header note). */}
          <span className="runtime-meta-process" data-process-kind={runtime.process_kind}>
            {runtime.process_kind}
          </span>
        </div>

        {isOwner && (
          <div className="runtime-card-actions" data-runtime-actions="owner">
            {/* A disabled agent cannot hold a plugin connection, so starting it
                could only fail. This card already omits what it cannot offer,
                so Start leaves the DOM rather than sitting there greyed out.
                Stop stays: stopping is always consistent with the intent. */}
            {!agentDisabled &&
              (status === 'registered' || status === 'stopped' || status === 'error') && (
                <button
                  className="btn btn-sm btn-primary"
                  data-runtime-action="start"
                  onClick={handleStart}
                  disabled={busy !== null}
                >
                  {busy === 'start' ? t(i18n)`Starting…` : t(i18n)`Start`}
                </button>
              )}
            {status === 'running' && (
              <button
                className="btn btn-sm"
                data-runtime-action="stop"
                onClick={handleStop}
                disabled={busy !== null}
              >
                {busy === 'stop' ? t(i18n)`Stopping…` : t(i18n)`Stop`}
              </button>
            )}
          </div>
        )}

        {isOwner && agentDisabled && (
          <div className="agent-inline-note" data-testid="runtime-disabled-note">
            <Trans>
              Enable {agent.display_name} before starting its runtime — a disabled agent cannot hold
              a plugin connection.
            </Trans>
          </div>
        )}

        {error && (
          <div className="runtime-card-error" role="alert">
            {i18n._(error)}
          </div>
        )}
      </div>
    </div>
  );
}

// Exported so tests can bind against the same label tables the card renders.
export const RUNTIME_STATUS_LABELS = STATUS_LABELS;
export const RUNTIME_STATUS_TONES = STATUS_TONES;
